import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  getCommerceWhatsAppService,
  getCommerceWhatsAppActivosService,
  getCommerceWhatsAppConPulsoService,
  buscarCommerceWhatsAppService,
  filtrarCommerceWhatsAppPorGrupoNOCService,
  getCommerceWhatsAppStatsService,
} from "modules/jarvis/services/whatsappService";

export const useWhatsappCopJarvis = () => {
  const [configuraciones, setConfiguraciones] = useState([]);
  const [configuracionesFiltradas, setConfiguracionesFiltradas] = useState([]);
  const [loading, setLoading] = useState({
    initial: false,
    filter: false,
    stats: false,
  });
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    activos: 0,
    inactivos: 0,
    conPulso: 0,
    sinPulso: 0,
    conTerminal: 0,
    sinTerminal: 0,
    gruposNOC: 0,
    workflows: 0,
    gruposNOCList: [],
    workflowsList: [],
  });
  const [filtros, setFiltros] = useState({
    grupo: "todos",
    activo: "todos",
    pulso: "todos",
    busqueda: "",
  });
  const [showNoData, setShowNoData] = useState(false);

  // Refs para timeouts y control de efectos
  const loadingTimeoutRef = useRef(null);
  const statsTimeoutRef = useRef(null);
  const isMounted = useRef(true);
  const applyingFiltersRef = useRef(false);
  const initialLoadRef = useRef(false);

  // Limpiar timeouts
  const clearTimeouts = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    if (statsTimeoutRef.current) {
      clearTimeout(statsTimeoutRef.current);
      statsTimeoutRef.current = null;
    }
  }, []);

  // Cargar estadísticas
  const loadStats = useCallback(async () => {
    if (!isMounted.current) return;

    setLoading((prev) => ({ ...prev, stats: true }));

    if (statsTimeoutRef.current) {
      clearTimeout(statsTimeoutRef.current);
    }

    const timeoutId = setTimeout(() => {
      if (isMounted.current) {
        setLoading((prev) => ({ ...prev, stats: false }));
      }
    }, 3000);

    statsTimeoutRef.current = timeoutId;

    try {
      const result = await getCommerceWhatsAppStatsService();

      if (statsTimeoutRef.current === timeoutId && isMounted.current) {
        clearTimeout(timeoutId);
        statsTimeoutRef.current = null;
      }

      if (result.success && isMounted.current) {
        setStats(result.data);
      }
    } catch (err) {
      console.error("Error al cargar estadísticas:", err);
    } finally {
      if (isMounted.current) {
        setLoading((prev) => ({ ...prev, stats: false }));
      }
    }
  }, []);

  // Cargar todas las configuraciones
  const loadConfiguraciones = useCallback(async () => {
    if (!isMounted.current) return;

    setLoading((prev) => ({ ...prev, initial: true }));
    setError(null);
    setShowNoData(false);

    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }

    const timeoutId = setTimeout(() => {
      if (isMounted.current) {
        setShowNoData(true);
        setLoading((prev) => ({ ...prev, initial: false }));
      }
    }, 5000);

    loadingTimeoutRef.current = timeoutId;

    try {
      const result = await getCommerceWhatsAppService();

      if (loadingTimeoutRef.current === timeoutId) {
        clearTimeout(timeoutId);
        loadingTimeoutRef.current = null;
      }

      if (!isMounted.current) return;

      if (result.success) {
        setConfiguraciones(result.data);
        setConfiguracionesFiltradas(result.data);
        setShowNoData(false);

        // Cargar estadísticas después de obtener los datos
        await loadStats();

        return result.data;
      } else {
        setError(result.error);
        return [];
      }
    } catch (err) {
      if (loadingTimeoutRef.current === timeoutId && isMounted.current) {
        clearTimeout(timeoutId);
        loadingTimeoutRef.current = null;
      }
      if (isMounted.current) {
        setError(err.message);
      }
      return [];
    } finally {
      if (isMounted.current) {
        setLoading((prev) => ({ ...prev, initial: false }));
      }
    }
  }, [clearTimeouts, loadStats]);

  // Aplicar filtros locales (sin llamadas a API) - ACTUALIZADO PARA INCLUIR groupName
  const aplicarFiltrosLocales = useCallback(() => {
    if (applyingFiltersRef.current || !configuraciones.length) return;

    applyingFiltersRef.current = true;

    try {
      let datosFiltrados = [...configuraciones];

      // Aplicar filtro de búsqueda - INCLUYE groupName
      if (filtros.busqueda) {
        const busquedaLower = filtros.busqueda.toLowerCase();
        datosFiltrados = datosFiltrados.filter(
          (c) =>
            c.nombreComercio?.toLowerCase().includes(busquedaLower) ||
            c.idCommerce?.toString().includes(busquedaLower) ||
            (c.idTerminal && c.idTerminal?.toString().includes(busquedaLower)) ||
            c.grupoAlertaNOC?.toLowerCase().includes(busquedaLower) ||
            c.groupName?.toLowerCase().includes(busquedaLower) || // AÑADIDO
            c.workflow?.toLowerCase().includes(busquedaLower) ||
            c.terminalName?.toLowerCase().includes(busquedaLower)
        );
      }

      // Aplicar filtro de estado
      if (filtros.activo === "activos") {
        datosFiltrados = datosFiltrados.filter((c) => c.activo === true);
      } else if (filtros.activo === "inactivos") {
        datosFiltrados = datosFiltrados.filter((c) => c.activo === false);
      }

      // Aplicar filtro de pulso
      if (filtros.pulso === "conPulso") {
        datosFiltrados = datosFiltrados.filter((c) => c.pulso === true);
      } else if (filtros.pulso === "sinPulso") {
        datosFiltrados = datosFiltrados.filter((c) => c.pulso === false);
      }

      // Aplicar filtro de grupo NOC
      if (filtros.grupo !== "todos") {
        datosFiltrados = datosFiltrados.filter((c) => c.grupoAlertaNOC === filtros.grupo);
      }

      setConfiguracionesFiltradas(datosFiltrados);
    } finally {
      applyingFiltersRef.current = false;
    }
  }, [configuraciones, filtros.busqueda, filtros.activo, filtros.pulso, filtros.grupo]);

  // Efecto para aplicar filtros cuando cambian
  useEffect(() => {
    // Pequeño retraso para evitar múltiples actualizaciones rápidas
    const timer = setTimeout(() => {
      aplicarFiltrosLocales();
    }, 300);

    return () => clearTimeout(timer);
  }, [filtros, aplicarFiltrosLocales]);

  // Efecto para limpiar timeouts al desmontar
  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      clearTimeouts();
    };
  }, [clearTimeouts]);

  // Cargar datos iniciales (solo una vez)
  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      loadConfiguraciones();
    }
  }, [loadConfiguraciones]);

  // Actualizar filtros
  const setFiltro = useCallback((tipo, valor) => {
    setFiltros((prev) => {
      // Evitar actualizaciones innecesarias si el valor no cambia
      if (prev[tipo] === valor) return prev;
      return {
        ...prev,
        [tipo]: valor,
      };
    });
  }, []);

  // Aplicar filtros con API (solo cuando es necesario)
  const aplicarFiltrosAPI = useCallback(async () => {
    if (
      !filtros.busqueda &&
      filtros.grupo === "todos" &&
      filtros.activo === "todos" &&
      filtros.pulso === "todos"
    ) {
      // Si no hay filtros, usar los datos locales
      setConfiguracionesFiltradas(configuraciones);
      return;
    }

    setLoading((prev) => ({ ...prev, filter: true }));
    setError(null);

    try {
      let resultado;

      // Aplicar filtros según corresponda
      if (filtros.busqueda) {
        resultado = await buscarCommerceWhatsAppService(filtros.busqueda);
      } else if (filtros.grupo !== "todos") {
        resultado = await filtrarCommerceWhatsAppPorGrupoNOCService(filtros.grupo);
      } else if (filtros.activo === "activos") {
        resultado = await getCommerceWhatsAppActivosService();
      } else if (filtros.pulso === "conPulso") {
        resultado = await getCommerceWhatsAppConPulsoService();
      } else {
        resultado = { success: true, data: configuraciones };
      }

      if (!isMounted.current) return;

      if (resultado.success) {
        let datosFiltrados = resultado.data;

        // Aplicar filtros adicionales localmente si es necesario
        if (filtros.activo === "inactivos" && !filtros.busqueda && filtros.grupo === "todos") {
          datosFiltrados = datosFiltrados.filter((c) => c.activo === false);
        }

        if (filtros.pulso === "sinPulso" && !filtros.busqueda && filtros.grupo === "todos") {
          datosFiltrados = datosFiltrados.filter((c) => c.pulso === false);
        }

        setConfiguracionesFiltradas(datosFiltrados);
      } else {
        setError(resultado.error);
      }
    } catch (err) {
      if (isMounted.current) {
        setError(err.message);
      }
    } finally {
      if (isMounted.current) {
        setLoading((prev) => ({ ...prev, filter: false }));
      }
    }
  }, [filtros, configuraciones]);

  // Refrescar datos
  const refreshData = useCallback(async () => {
    clearTimeouts();
    setShowNoData(false);
    await loadConfiguraciones();
  }, [clearTimeouts, loadConfiguraciones]);

  // Obtener grupos NOC únicos para filtros
  const gruposNOCUnicos = useMemo(() => {
    return [...new Set(configuraciones.map((c) => c.grupoAlertaNOC).filter(Boolean))].sort();
  }, [configuraciones]);

  // Obtener grupos Nombre únicos para posibles filtros futuros - NUEVO
  const gruposNombreUnicos = useMemo(() => {
    return [...new Set(configuraciones.map((c) => c.groupName).filter(Boolean))].sort();
  }, [configuraciones]);

  // Obtener workflows únicos para filtros
  const workflowsUnicos = useMemo(() => {
    return [...new Set(configuraciones.map((c) => c.workflow).filter(Boolean))].sort();
  }, [configuraciones]);

  // Calcular estadísticas actualizadas basadas en los datos - INCLUYE groupName
  const statsActualizadas = useMemo(() => {
    const total = configuraciones.length;
    const activos = configuraciones.filter((c) => c.activo === true).length;
    const inactivos = configuraciones.filter((c) => c.activo === false).length;
    const conPulso = configuraciones.filter((c) => c.pulso === true).length;
    const sinPulso = configuraciones.filter((c) => c.pulso === false).length;
    const conTerminal = configuraciones.filter(
      (c) => c.idTerminal !== null && c.idTerminal !== undefined && c.idTerminal !== 0
    ).length;
    const sinTerminal = configuraciones.filter(
      (c) => c.idTerminal === null || c.idTerminal === undefined || c.idTerminal === 0
    ).length;

    // Contar configuraciones con groupName válido - NUEVO
    const conGroupName = configuraciones.filter(
      (c) => c.groupName && c.groupName !== "Sin grupo" && c.groupName !== null
    ).length;

    const sinGroupName = configuraciones.filter(
      (c) => !c.groupName || c.groupName === "Sin grupo" || c.groupName === null
    ).length;

    return {
      total,
      activos,
      inactivos,
      conPulso,
      sinPulso,
      conTerminal,
      sinTerminal,
      conGroupName, // NUEVO
      sinGroupName, // NUEVO
      gruposNOC: gruposNOCUnicos.length,
      gruposNombre: gruposNombreUnicos.length, // NUEVO
      workflows: workflowsUnicos.length,
      gruposNOCList: gruposNOCUnicos,
      gruposNombreList: gruposNombreUnicos, // NUEVO
      workflowsList: workflowsUnicos,
    };
  }, [configuraciones, gruposNOCUnicos, gruposNombreUnicos, workflowsUnicos]);

  // Determinar si está cargando
  const isLoading = loading.initial || loading.filter || loading.stats;

  return {
    // Estado
    configuraciones: configuracionesFiltradas,
    todasConfiguraciones: configuraciones,
    loading,
    error,
    stats: stats.total ? stats : statsActualizadas,
    showNoData,
    filtros,
    gruposNOC: gruposNOCUnicos,
    gruposNombre: gruposNombreUnicos, // NUEVO
    workflows: workflowsUnicos,

    // Funciones
    loadConfiguraciones,
    refreshData,
    setFiltro,
    aplicarFiltros: aplicarFiltrosAPI,

    // Utilidades
    isLoading,
    hasData: configuracionesFiltradas.length > 0,
    totalConfiguraciones: configuracionesFiltradas.length,
  };
};
